import { MdClose } from "react-icons/md";
import { format } from "date-fns";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { Check } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { toast } from "sonner";
import { useMemo } from "react";

import {
  AlertDialog,
  AlertDialogContent,
} from "@/components/ui/alert-dialog/alert-dialog";
import { useTodoViewModal } from "./hooks/use-todo-view-modal";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion/accordion";

import { Button } from "@/components/ui/button/button";
import {
  changeStatusOfSubtask,
  changeStatusOfTODO,
} from "@/config/mutation/employee/todo.mutation";
// import { getStatusOfTodoForEmployee } from "@/config/queries/employee/emp-todo-queries";
import {
  getStatusOfTodoForEmployeeQueryOptions,
  getTodoForEmployeeQueryOptions,
} from "@/config/queries/emp-query-options";
import { useAuth } from "@/lib/use-auth";
import { Separator } from "@/components/ui/separator/separator";

export const TodoViewModal = () => {
  const { open, onOpenChange, setTodo, todo } = useTodoViewModal();
  const empKey = Cookies.get("key") as string;
  const queryClient = useQueryClient();

  const { data: authData } = useAuth();

  const {
    data,
    isPending: isTodoPending,
    isFetching: isTodoFetching,
  } = useQuery(getTodoForEmployeeQueryOptions(empKey, todo?.id as string));

  const {
    data: todoStatusData,
    isPending: isTodoStatusPending,
    isFetching: isTodosStatusFetching,
  } = useQuery(
    getStatusOfTodoForEmployeeQueryOptions(empKey, todo?.id as string)
  );

  const oneTodoData = useMemo(() => {
    if (isTodoPending || isTodoStatusPending) {
      return {};
    }

    if (
      data === undefined ||
      data?.todo === undefined ||
      todoStatusData === undefined ||
      todoStatusData?.foundstatus === undefined
    ) {
      return {};
    }

    const result = {
      ...data?.todo,
      column:
        todoStatusData?.foundstatus?.find(
          (s) => s?.employee?._id === authData?.user?._id
        )?.status || "Pending",
    };

    return result;
  }, [
    isTodoPending,
    isTodoFetching,
    data,
    isTodoStatusPending,
    isTodosStatusFetching,
    todoStatusData,
  ]) as {
    column: string | undefined;
    createdAt: string | Date;
    deadline: string | Date;
    description: string;
    files?: string[] | undefined;
    title: string;
    tasks?:
      | {
          _id: string;
          createdAt: string | Date;
          description: string;
          files?: string[] | undefined;
          status: string;
        }[]
      | undefined;
  };

  const { mutateAsync, isPending } = useMutation({
    mutationFn: changeStatusOfTODO(empKey),
  });

  const { mutateAsync: mutateTaskStatus } = useMutation({
    mutationFn: changeStatusOfSubtask(empKey),
  });

  const changeTodoStatus = async (status: string) => {
    await mutateAsync(
      {
        status,
        todoId: todo?.id as string,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["employee-todo"],
          });
          queryClient.invalidateQueries({
            queryKey: ["employee-todos"],
          });
          queryClient.invalidateQueries({
            queryKey: ["employee-todo-status", todo?.id],
          });

          toast.success("Todo Status Updated!");
        },
      }
    );
  };

  const changeTodoTaskStatus = async (status: string, taskId: string) => {
    await mutateTaskStatus(
      {
        status,
        taskId,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["employee-todo"],
          });
          queryClient.invalidateQueries({
            queryKey: ["employee-todos"],
          });
          queryClient.invalidateQueries({
            queryKey: ["employee-todo-status", taskId],
          });

          toast.success("Subtask Status Updated!");
        },
      }
    );
  };

  const colors: {
    [key: string]: string;
  } = {
    Pending: "text-neutral-500",
    Started: "text-yellow-500",
    "Update Required": "text-blue-500",
    Testing: "text-emerald-400",
    Finished: "text-green-500",
  };

  return (
    <>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="p-0 max-w-7xl">
          <div>
            <div className="border-b px-3 py-1 flex items-center justify-end">
              <div className="relative">
                <button
                  type="button"
                  className="p-1 rounded-md transition-all duration-300 hover:bg-accent"
                  onClick={() => {
                    onOpenChange(false);
                    setTodo(null);
                  }}
                >
                  <MdClose className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex">
              <div className="flex-[0.6] p-5 space-y-4">
                <h1 className="text-3xl font-semibold">{oneTodoData?.title}</h1>

                <p className="text-base">{oneTodoData?.description}</p>

                <Separator className="my-3" />

                <div className="flex flex-col">
                  <h3 className="text-lg font-medium">Subtasks</h3>
                  <Accordion type="single" collapsible>
                    {oneTodoData?.tasks?.map((task, index) => {
                      return (
                        <AccordionItem value={`item-${index + 1}`} className="">
                          <AccordionTrigger className="truncate tracking-normal">
                            {task?.description}
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="flex flex-col">
                              <div className="flex items-center justify-between">
                                <p>{task?.description}</p>

                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
                                      disabled={isPending}
                                    >
                                      <DotsHorizontalIcon className="h-4 w-4" />
                                      <span className="sr-only">Open menu</span>
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent
                                    align="end"
                                    className="w-[160px]"
                                  >
                                    <DropdownMenuSub>
                                      <DropdownMenuSubTrigger>
                                        Change Status
                                      </DropdownMenuSubTrigger>
                                      <DropdownMenuPortal>
                                        <DropdownMenuSubContent className="w-[200px]">
                                          {[
                                            "Pending",
                                            "Started",
                                            "Update Required",
                                            "Testing",
                                            "Finished",
                                          ].map((status) => {
                                            return (
                                              <DropdownMenuItem
                                                key={status}
                                                className="flex items-center gap-2 justify-between"
                                                onMouseDown={() => {
                                                  changeTodoTaskStatus(
                                                    status,
                                                    task?._id as string
                                                  );
                                                }}
                                              >
                                                {status}

                                                {status === task?.status && (
                                                  <Check className="w-3 h-3" />
                                                )}
                                              </DropdownMenuItem>
                                            );
                                          })}
                                        </DropdownMenuSubContent>
                                      </DropdownMenuPortal>
                                    </DropdownMenuSub>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>

                              <p
                                className={cn(
                                  colors[task?.status as string],
                                  "font-medium"
                                )}
                              >
                                {task?.status}
                              </p>

                              {task?.files && task?.files?.length > 0 && (
                                <div className="flex flex-col gap-2 mt-4">
                                  {task?.files?.map((file, index) => {
                                    return (
                                      <div className="border rounded-md border-dashed transition-all duration-300 hover:bg-accent">
                                        <a
                                          href={file}
                                          target="_blank"
                                          className="px-3 py-1.5 w-full inline-block rounded-md"
                                        >
                                          Attachment {index + 1}
                                        </a>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                </div>
              </div>
              <div className="flex-[0.4] border-l p-5 space-y-4">
                <div className="flex flex-col gap-5">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <p>Status : </p>
                      <p
                        className={cn(
                          colors[oneTodoData?.column as string],
                          "font-medium"
                        )}
                      >
                        {oneTodoData?.column}
                      </p>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
                          disabled={isPending}
                        >
                          <DotsHorizontalIcon className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[160px]">
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            Change Status
                          </DropdownMenuSubTrigger>
                          <DropdownMenuPortal>
                            <DropdownMenuSubContent className="w-[200px]">
                              {[
                                "Pending",
                                "Started",
                                "Update Required",
                                "Testing",
                                "Finished",
                              ].map((status) => {
                                return (
                                  <DropdownMenuItem
                                    key={status}
                                    className="flex items-center gap-2 justify-between"
                                    onMouseDown={() => {
                                      changeTodoStatus(status);
                                    }}
                                  >
                                    {status}

                                    {status === oneTodoData?.column && (
                                      <Check className="w-3 h-3" />
                                    )}
                                  </DropdownMenuItem>
                                );
                              })}
                            </DropdownMenuSubContent>
                          </DropdownMenuPortal>
                        </DropdownMenuSub>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {oneTodoData?.deadline && (
                    <div className="flex items-center gap-1">
                      <p>Deadline : </p>
                      <p>
                        {format(
                          new Date(oneTodoData?.deadline as string),
                          "PPP"
                        )}
                      </p>
                    </div>
                  )}

                  {oneTodoData?.files && oneTodoData?.files?.length > 0 && (
                    <div className="flex flex-col gap-3 mt-4">
                      {oneTodoData?.files?.map((file, index) => {
                        return (
                          <div className="border rounded-md border-dashed transition-all duration-300 hover:bg-accent">
                            <a
                              href={file}
                              target="_blank"
                              className="px-3 py-1.5 w-full inline-block rounded-md"
                            >
                              Attachment {index + 1}
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
